import { useEffect } from 'react';
import { LegalLayout } from './LegalLayout';

const EFFECTIVE_DATE = 'May 13, 2026';
const CONTACT_EMAIL = 'hello@sendfast.ai';

/**
 * Terms of Service — placeholder copy that mirrors the current product
 * surface (free + Pro tiers, AI-generated email, transactional sends via
 * Resend, MCP API for agents). Same disclaimer as Privacy: this is a
 * good-faith starting point, not a substitute for vetted counsel.
 */
export function TermsPage() {
  useEffect(() => {
    document.title = 'Terms of Service — SendFast';
  }, []);

  return (
    <LegalLayout title="Terms of Service" effectiveDate={EFFECTIVE_DATE}>
      <p>
        These Terms of Service (&ldquo;Terms&rdquo;) govern your use of SendFast
        (&ldquo;Service&rdquo;), operated by SendFast (&ldquo;we&rdquo;, &ldquo;us&rdquo;). By
        creating an account or using the Service, you agree to these Terms. If you do not agree,
        do not use the Service.
      </p>

      <h2>1. The Service</h2>
      <p>
        SendFast helps you draft, design, and send marketing email. It includes a web app, an
        HTTP API, and an MCP integration intended for AI agents. Features and limits depend on
        the plan you choose.
      </p>

      <h2>2. Your account</h2>
      <ul>
        <li>You must be at least 16 years old and able to form a binding contract.</li>
        <li>
          You are responsible for keeping your credentials and API keys secure, and for all
          activity that occurs under your account.
        </li>
        <li>You must provide accurate registration information and keep it current.</li>
      </ul>

      <h2>3. Acceptable use</h2>
      <p>You agree <strong>not</strong> to use the Service to:</p>
      <ul>
        <li>
          Send unsolicited bulk email (&ldquo;spam&rdquo;), or any email that violates the
          CAN-SPAM Act, GDPR, PECR, CASL, or any other applicable anti-spam or privacy law.
        </li>
        <li>
          Send emails that are deceptive, fraudulent, defamatory, harassing, or that infringe
          intellectual property rights.
        </li>
        <li>
          Send phishing emails, malware, or any content intended to compromise the security or
          privacy of recipients.
        </li>
        <li>
          Send to recipients who have not consented to receive email from you, or who have
          unsubscribed.
        </li>
        <li>
          Reverse engineer, scrape, or interfere with the Service, or attempt to bypass usage
          limits.
        </li>
        <li>Use the Service to send adult content, gambling promotions, or illegal goods.</li>
      </ul>
      <p>
        We may suspend or terminate accounts that violate this section, with or without notice,
        and may report violations to relevant authorities.
      </p>

      <h2>4. AI-generated content</h2>
      <p>
        Some features generate email content using third-party AI models. Output is provided
        &ldquo;as is&rdquo; and may contain errors, inaccuracies, or unintended phrasing. You
        are solely responsible for reviewing AI-generated content before sending it. You retain
        ownership of the content you produce, subject to the rights you grant us in Section 6.
      </p>

      <h2>5. Plans, billing, and fair use</h2>
      <ul>
        <li>
          Free plans have monthly send and generation limits as shown on the pricing page. Paid
          plans (Pro, Scale) are billed in advance on a monthly or annual basis.
        </li>
        <li>
          You may cancel a paid plan at any time. Cancellation takes effect at the end of the
          current billing period; we do not refund partial periods unless required by law.
        </li>
        <li>
          We may change pricing or plan limits with at least 30 days&apos; notice for paid
          users.
        </li>
        <li>
          We reserve the right to throttle or block usage that materially exceeds typical usage
          patterns or that is consistent with abuse.
        </li>
      </ul>

      <h2>6. Your content</h2>
      <p>
        You retain all rights to the content you upload or generate (drafts, templates, lists,
        AI prompts). You grant us a limited, worldwide, non-exclusive license to host, copy,
        process, and transmit that content solely as needed to operate the Service for you. We
        do not use your content to train models or sell it to third parties.
      </p>

      <h2>7. Suspension and termination</h2>
      <p>
        You may delete your account at any time from the dashboard. We may suspend or terminate
        your access if you breach these Terms, fail to pay fees due, or if continued provision
        of the Service is unlawful. We will give reasonable notice when practical.
      </p>

      <h2>8. Disclaimers</h2>
      <p>
        The Service is provided &ldquo;as is&rdquo; and &ldquo;as available&rdquo;, without
        warranties of any kind, express or implied, including merchantability, fitness for a
        particular purpose, and non-infringement. We do not guarantee that the Service will be
        uninterrupted, error-free, or that any specific email will be delivered.
      </p>

      <h2>9. Limitation of liability</h2>
      <p>
        To the maximum extent permitted by law, SendFast and its affiliates will not be liable
        for any indirect, incidental, special, consequential, or punitive damages, or any loss
        of profits, data, goodwill, or other intangible losses, arising out of or related to
        your use of the Service. Our aggregate liability for any claim arising out of these
        Terms will not exceed the amount you paid us in the 12 months preceding the claim, or
        US $100, whichever is greater.
      </p>

      <h2>10. Indemnification</h2>
      <p>
        You agree to indemnify and hold harmless SendFast and its affiliates from any claim,
        loss, or expense (including reasonable legal fees) arising out of your content, your use
        of the Service, or your violation of these Terms or applicable law.
      </p>

      <h2>11. Changes to these Terms</h2>
      <p>
        We may update these Terms from time to time. If we make material changes, we will
        notify you by email or via an in-app notice at least 14 days before they take effect.
        Continued use of the Service after the effective date constitutes acceptance.
      </p>

      <h2>12. Governing law</h2>
      <p>
        These Terms are governed by the laws of the jurisdiction in which SendFast is
        established, without regard to conflict-of-laws principles. Any dispute will be heard
        in the courts of that jurisdiction, except where applicable consumer-protection law
        gives you other rights.
      </p>

      <h2>13. Contact</h2>
      <p>
        Questions about these Terms? Email{' '}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
      </p>
    </LegalLayout>
  );
}
